/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import moment from 'moment';
import { initialState } from './reducer';
import { FORM_NAME, CAR_TABLE_ROWS_NAMES, TYPES_WITH_DATE_ISSUE_AND_MODES, TYPES_WITH_TRAILER } from './constants';
import { getLocalStorageItem } from '../../utils/localStorage';

export const selectCarsModalDomain = state => state.carsModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileDomain = state => state.profileModalReducer || {};

export const getCarInfoSelector = () =>
	createSelector(selectCarsModalDomain, ({ carInfo }) => {
		if (!carInfo) {
			return [];
		}

		const modesNames = {};

		const modes = {};

		carInfo?.modes.forEach((el, index) => {
			modesNames[`mode${index + 1}`] = `${el.mode.name} °C`;
			modes[`mode${index + 1}`] = {
				validate: el?.is_validated ? 'Да' : 'Нет',
				date: el?.date ? moment(el?.date).format('DD.MM.YYYY') : 'Нет',
				...(el?.is_approved !== null ? { approve: el?.is_approved ? 'Да' : 'Нет' } : {}),
			};
			CAR_TABLE_ROWS_NAMES[`mode${index + 1}`] = `Температурный режим #${index + 1}`;
		});

		const data = {
			...(carInfo?.company
				? {
						company: {
							name: carInfo.company?.name || '-',
							role: carInfo.company?.type.name || '-',
							id: carInfo.company?.id || '',
						},
				  }
				: {}),
			...(carInfo?.legal
				? {
						legal: {
							name: carInfo.legal?.name || '-',
							inn: carInfo.legal?.inn || '-',
							id: carInfo.legal?.id || '',
						},
				  }
				: {}),
			number: carInfo.number || '-',
			vin: carInfo.vin || '-',
			type: carInfo.type?.name || '-',
			dateIssue: carInfo.date_issue ? moment(carInfo.date_issue).format('MM.YYYY') : '-',
			...(carInfo.type_date_issue ? { typeDateIssue: moment(carInfo.type_date_issue).format('MM.YYYY') } : {}),
			weight: carInfo.weight ? `${carInfo.weight} тн` : '-',
			volume: carInfo.volume ? `${carInfo.volume} м³` : '-',
			ownership: carInfo.ownership?.name ? carInfo.ownership?.name : '-',
			...(carInfo.ownership && carInfo.ownership?.id !== 1 && carInfo.ownership?.id !== 2
				? {
						docName: carInfo.doc_name ? carInfo.doc_name : '-',
						docNumber: carInfo.doc_number ? carInfo.doc_number : '-',
						docDate: carInfo.doc_date ? moment(carInfo.doc_date).format('DD.MM.YYYY') : '-',
				  }
				: {}),
			...(carInfo.trailer?.name ? { trailer: `${carInfo.trailer?.name}, ${carInfo.trailer?.number}` } : {}),
			...(carInfo.is_accredited !== null ? { isAccredited: carInfo.is_accredited ? 'Да' : 'Нет' } : {}),
			...modesNames,
			...(carInfo.user
				? {
						author: {
							name: `${carInfo.user?.last_name || '-'} ${carInfo.user?.first_name || '-'}`,
							role: carInfo.user?.role.name || '-',
						},
				  }
				: {}),
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			return [...result, { id: key, title: CAR_TABLE_ROWS_NAMES[key], value, mode: modes[key] }];
		}, []);
	});

export const getIsCarInfoLoadingSelector = () =>
	createSelector(
		selectCarsModalDomain,
		({ isCarInfoLoading, isCarDeleting, isCarSaving }) => isCarInfoLoading || isCarDeleting || isCarSaving,
	);

export const getCarNameSelector = () => createSelector(selectCarsModalDomain, ({ carInfo }) => carInfo?.name || '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car');
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car' && query?.tab === 'audit');
	});

export const getIsEventTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car' && query?.tab === 'event');
	});

export const getActiveCarIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => (query?.modal === 'car' ? query?.id : null));

export const getIsCarAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car' && query?.mode === 'add');
	});

export const getIsCarEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car' && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getCarTypeIdSelector = () => createSelector(selectCarsModalDomain, ({ carInfo }) => carInfo?.type?.id);

export const getFormInitialValuesSelector = () =>
	createSelector(selectCarsModalDomain, ({ carInfo }) => {
		if (!carInfo) {
			const companyId = getLocalStorageItem('user_data').companyId;

			return {
				ownership_id: null,
				modes: [{}],
				legal_id: companyId,
			};
		}

		return {
			legal_id: carInfo.legal?.id,
			number: carInfo.number,
			vin: carInfo.vin,
			name: carInfo.name,
			date_issue: carInfo.date_issue,
			type_date_issue: carInfo.type_date_issue,
			trailer: carInfo.trailer?.id ?? null,
			weight: carInfo.weight,
			volume: carInfo.volume,
			ownership_id: carInfo.ownership?.id ? carInfo.ownership?.id : null,
			...(carInfo.ownership && carInfo.ownership?.id !== 1 && carInfo.ownership?.id !== 2
				? {
						doc_name: carInfo.doc_name,
						doc_number: carInfo.doc_number,
						doc_date: carInfo.doc_date ? moment(carInfo.doc_date).format('YYYY-MM-DD') : '',
				  }
				: {}),
			is_accredited: carInfo.is_accredited,
			modes: carInfo.modes?.length
				? carInfo.modes?.map(mode => ({
						id: mode.id,
						mode_id: String(mode.mode?.id),
						is_validated: mode.is_validated,
						date: mode.date ? moment(mode.date).format('YYYY-MM-DD') : '',
				  }))
				: [{}],
			type_id: String(carInfo.type?.id),
		};
	});

export const getDataForCreateOrUpdateCarSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		const result = {
			legal_id: formValues.legal_id,
			name: formValues.name,
			number: formValues.number,
			vin: formValues.vin,
			type_id: formValues.type_id,
			date_issue: formValues.date_issue,
			weight: formValues.weight,
			volume: formValues.volume,
			ownership_id: formValues.ownership_id,
			doc_name: formValues.doc_name,
			doc_number: formValues.doc_number,
			doc_date: formValues.doc_date,
			is_accredited: formValues.is_accredited,
		};

		if (TYPES_WITH_DATE_ISSUE_AND_MODES.includes(formValues.type_id)) {
			result.type_date_issue = formValues.type_date_issue;

			const modes = formValues.modes?.reduce((acc, mode) => {
				if (!isEmpty(mode)) {
					acc.push({ ...mode, is_validated: Boolean(mode.is_validated) });
				}

				return acc;
			}, []);

			if (modes.length) {
				result.modes = modes;
			}
		}

		if (TYPES_WITH_TRAILER.includes(formValues.type_id)) {
			result.trailer_id = formValues.trailer;
		}

		return result;
	});

export const getTrailersListSelector = () => createSelector(selectCarsModalDomain, ({ trailersList }) => trailersList);

export const getIsTrailersListLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isTrailersListLoading }) => isTrailersListLoading);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getOwnershipsSelector = () => createSelector(selectCarsModalDomain, ({ ownerships }) => ownerships);

export const getLegalsSelector = () => createSelector(selectCarsModalDomain, ({ legals }) => legals);

export const getIsLegalsLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isLegalsLoading }) => isLegalsLoading);

export const getCarTypesSelector = () => createSelector(selectCarsModalDomain, ({ carTypes }) => carTypes);

export const getModesSelector = () => createSelector(selectCarsModalDomain, ({ modes }) => modes);

export const getIsOwnershipsLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isOwnershipsLoading }) => isOwnershipsLoading);

export const getIsCarTypesLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isCarTypesLoading }) => isCarTypesLoading);

export const getIsModesLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isModesLoading }) => isModesLoading);

export const getIsTypeDateIssueVisibleSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return TYPES_WITH_DATE_ISSUE_AND_MODES.includes(formValues?.type_id);
	});

export const getIsTrailerVisibleSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return TYPES_WITH_TRAILER.includes(formValues?.type_id);
	});

export const getIsOwnershipVisibleSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return formValues?.ownership_id && formValues?.ownership_id !== 1 && formValues.ownership_id !== 2;
	});

export const getIsAdminSelector = () =>
	createSelector(selectProfileDomain, ({ userInfo }) => {
		return userInfo?.company?.id === 1;
	});

export const getIsAvailableTrailersSelector = () =>
	createSelector(selectProfileDomain, ({ userRules }) => {
		return Boolean(userRules?.trailers?.create);
	});

export const getCarTabsSelector = () => createSelector(selectCarsModalDomain, ({ carInfo }) => carInfo?.tabs || {});

export const getEventDataSelector = () => createSelector(selectCarsModalDomain, ({ eventData }) => eventData);

export const getIsEventDataLoadingSelector = () =>
	createSelector(selectCarsModalDomain, ({ isEventDataLoading }) => isEventDataLoading);

export const getIsAccreditationTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'car' && query?.tab === 'accreditation');
	});

export const getCarActionsSelector = () =>
	createSelector(selectCarsModalDomain, ({ carInfo }) => carInfo?.actions || {});
